import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ACTIVE_INCENTIVES } from '@/config/queries';
import { usePoolData } from './usePoolData';
import { calculateIncentiveAPR, getIncentiveKey, sortIncentivesByApr, getAprRate } from '@/utils/common';
import { keccak256 } from 'ethers';
import { IncentiveKey } from '@/types';
import { useIncentiveStakingData } from './useIncentiveStakingData';

export interface IncentiveWithAPR {
  incentive: IncentiveKey;
  incentiveId: string;
  apr: number;
  aprRate: number; // reward/duration for sorting
  isLive: boolean;
  reward: number;
  endTime: Date;
}

interface UseAllIncentivesWithAPRResult {
  incentives: IncentiveWithAPR[];
  isLoading: boolean;
}

/**
 * Hook to get all incentives with their APR calculated
 * Returns incentives sorted by APR (highest first)
 */
export function useAllIncentivesWithAPR(): UseAllIncentivesWithAPRResult {
  const poolData = usePoolData();

  // Fetch all active incentives
  const { data: incentivesData, loading: loadingIncentives } = useQuery(
    GET_ACTIVE_INCENTIVES(poolData.poolAddress as string),
    { skip: !poolData.poolAddress }
  );

  // Parse and sort incentives
  const parsedIncentives: IncentiveKey[] = useMemo(() => {
    if (!incentivesData?.incentiveInfos) return [];
    return incentivesData.incentiveInfos.map((i: any) => ({
      id: i.id,
      rewardToken: i.rewardToken,
      pool: i.pool,
      startTime: BigInt(i.startTime),
      endTime: BigInt(i.endTime),
      refundee: i.refundee,
      reward: BigInt(i.reward),
    }));
  }, [incentivesData]);

  // Sort by APR rate
  const sortedIncentives = useMemo(() => sortIncentivesByApr(parsedIncentives), [parsedIncentives]);

  // Get the first incentive's staking data (for APR calculation base)
  // All incentives in the same pool share the same totalStakedValue
  const firstIncentiveId = sortedIncentives[0]
    ? keccak256(getIncentiveKey(sortedIncentives[0]))
    : '';
  const { totalStakedValue, isLoading: loadingStaking } = useIncentiveStakingData(firstIncentiveId);

  // Calculate APR for all incentives
  const incentivesWithAPR: IncentiveWithAPR[] = useMemo(() => {
    if (sortedIncentives.length === 0) return [];

    const nowSeconds = Math.floor(Date.now() / 1000);

    return sortedIncentives.map((incentive) => {
      const incentiveId = keccak256(getIncentiveKey(incentive));
      const isLive = nowSeconds <= Number(incentive.endTime);

      let apr = 0;
      // Calculate APR only when we have the required data
      if (totalStakedValue > 0 && isLive && poolData.jocxPrice > 0) {
        apr = calculateIncentiveAPR(
          incentive.reward || BigInt(0),
          poolData.jocxPrice,
          totalStakedValue,
          incentive.startTime,
          incentive.endTime
        );
      }

      return {
        incentive,
        incentiveId,
        apr,
        aprRate: getAprRate(incentive),
        isLive,
        reward: Number(incentive.reward) / 1e18,
        endTime: new Date(Number(incentive.endTime) * 1000),
      };
    });
  }, [sortedIncentives, totalStakedValue, poolData.jocxPrice]);

  // Re-sort by actual APR (in case staked values differ)
  const sortedByAPR = useMemo(() => {
    return [...incentivesWithAPR].sort((a, b) => {
      // Live incentives first
      if (a.isLive !== b.isLive) return b.isLive ? 1 : -1;
      // Then by APR (higher first)
      return b.apr - a.apr;
    });
  }, [incentivesWithAPR]);

  return {
    incentives: sortedByAPR,
    isLoading: loadingIncentives || loadingStaking || poolData.isLoading,
  };
}
